import { createContext, useContext } from "react";

import { useColorScheme } from "./ambient.ts";
import { MARBLE_DRIFT_SPEED, MARBLE_DRIFT_WARP, MARBLE_WARP, WARP_SPEED } from "./warp-presets.ts";

// What the page's Warps paint with and how they move, and the live overrides
// a tuning panel can lay over them. The production effects (the marble, the
// AI button) read from here; the tuner only writes the overrides.

export type MarbleFlow = "veins" | "drift";

/** Order is the pattern: neighbours in the list meet on screen. Cream ends it so it pools as the ground. */
const DAY_TOKENS = [
  "marble-chartreuse",
  "marble-olive",
  "marble-wine",
  "marble-plum",
  "marble-magenta",
  "marble-mauve",
  "marble-lilac",
  "marble-blush",
  "marble-rose",
  "marble-cream",
] as const;

/** At night the marble is painted in Deep tide: near-black, navy, blue, rust and brick. */
const NIGHT_TOKENS = ["tide-1", "tide-2", "tide-3", "tide-4", "tide-5"] as const;

/** One look of the marble: its paint and how it moves. The flow picks the motion by day and by night; the scheme picks the paint. */
export type MarbleLook = MarbleFlow | `${MarbleFlow}-night`;

/** Veins: fine marbled streaks, slow. Drift: wide soft pools, quick. */
const DAY_LOOKS = {
  veins: { ...MARBLE_WARP, speed: WARP_SPEED },
  drift: { ...MARBLE_DRIFT_WARP, speed: MARBLE_DRIFT_SPEED },
} as const;

export type ResolvedLook = {
  readonly name: MarbleLook;
  readonly tokens: readonly string[];
  readonly settings: MarbleSettings;
};

/** At night the veins lie at their own angle; everything else moves as by day. */
const NIGHT_ROTATION = 25;

/** The look a flow shows in the page's current scheme: the flow's own motion, day or night paint. */
export function useMarbleLook(flow: MarbleFlow): ResolvedLook {
  const settings = DAY_LOOKS[flow];
  if (useColorScheme() === "dark") {
    return {
      name: `${flow}-night`,
      tokens: NIGHT_TOKENS,
      settings: { ...settings, rotation: NIGHT_ROTATION },
    };
  }
  return { name: flow, tokens: DAY_TOKENS, settings };
}

/** Whether a look is painted for the night. */
export function isNightLook(look: MarbleLook): boolean {
  return look.endsWith("-night");
}

export const SHAPES = ["checks", "stripes", "edge"] as const;
export type Shape = (typeof SHAPES)[number];

export type MarbleSettings = {
  readonly proportion: number;
  readonly softness: number;
  readonly distortion: number;
  readonly swirl: number;
  readonly swirlIterations: number;
  readonly shape: Shape;
  readonly shapeScale: number;
  readonly speed: number;
  readonly scale: number;
  readonly rotation: number;
};

export type Tuning = {
  readonly settings: MarbleSettings;
  readonly colors: readonly string[] | null;
  /** The veil, the header band and the fades over the marble; on unless a tuner turns them off. */
  readonly overlays?: boolean;
};

export const TuningContext = createContext<Tuning | null>(null);
export const SparkTuningContext = createContext<Tuning | null>(null);

/** The tuner's live settings, when a tuner is open around the backdrop. */
export function useMarbleTuning(): Tuning | null {
  return useContext(TuningContext);
}

/** The colours the AI button's Warp paints with. */
export const SPARK_TOKENS = ["spark-1", "spark-2", "spark-3"] as const;

/** The tuner's live settings for the AI button, when a tuner is open around it. */
export function useSparkTuning(): Tuning | null {
  return useContext(SparkTuningContext);
}
